import { useState, useEffect } from 'react';
import { 
  LogOut, Users, FileText, CheckCircle2, XCircle, Clock, 
  DollarSign, Download, Search, Filter, Eye, Edit2, 
  ChevronDown, ChevronUp, AlertCircle, Building2, Mail,
  Phone, Globe, MapPin, Calendar, RefreshCw
} from 'lucide-react';
import './AdminDashboard.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function AdminDashboard() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState('');
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const [applications, setApplications] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedApp, setSelectedApp] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [updateData, setUpdateData] = useState({});

  useEffect(() => {
    const savedToken = localStorage.getItem('adminToken');
    if (savedToken) {
      verifyToken(savedToken);
    }
  }, []);

  const verifyToken = async (tkn) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/verify?token=${tkn}`);
      if (response.ok) {
        setToken(tkn);
        setIsAuthenticated(true);
        fetchData(tkn);
      } else {
        localStorage.removeItem('adminToken');
      }
    } catch (error) {
      localStorage.removeItem('adminToken');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setLoginError('');
    
    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginData)
      });
      
      const result = await response.json();
      
      if (response.ok) {
        localStorage.setItem('adminToken', result.token);
        setToken(result.token);
        setIsAuthenticated(true);
        fetchData(result.token);
      } else {
        setLoginError(result.detail || 'Invalid credentials');
      }
    } catch (error) {
      setLoginError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setIsAuthenticated(false);
    setToken('');
    setApplications([]);
    setStats(null);
  };

  const fetchData = async (tkn) => {
    setIsLoading(true);
    try {
      const [appsRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/partner-applications?token=${tkn}`),
        fetch(`${API_URL}/api/admin/partner-stats?token=${tkn}`)
      ]);
      
      if (appsRes.ok) {
        const appsData = await appsRes.json();
        setApplications(appsData.applications || []);
      }
      
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateApplication = async () => {
    if (!selectedApp) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/admin/partner-applications/${selectedApp.id}?token=${token}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updateData)
        }
      );
      
      if (response.ok) {
        fetchData(token);
        setEditMode(false);
        setUpdateData({});
        setSelectedApp(null);
      }
    } catch (error) {
      console.error('Failed to update application:', error);
    }
  };

  const handleExportCSV = () => {
    window.open(`${API_URL}/api/admin/export-partners?token=${token}`, '_blank');
  };

  const getStatusColor = (status) => {
    const colors = {
      new: '#3b82f6',
      under_review: '#f59e0b',
      approved: '#10b981',
      rejected: '#ef4444',
      agreement_pending: '#8b5cf6',
      payment_pending: '#f97316',
      active: '#2dd4bf',
      expired: '#6b7280'
    };
    return colors[status] || '#6b7280';
  };

  const getStatusLabel = (status) => {
    const labels = {
      new: 'New',
      under_review: 'Under Review',
      approved: 'Approved',
      rejected: 'Rejected',
      agreement_pending: 'Agreement Pending',
      payment_pending: 'Payment Pending',
      active: 'Active',
      expired: 'Expired'
    };
    return labels[status] || status;
  };

  const filteredApplications = applications.filter(app => {
    const matchesStatus = filterStatus === 'all' || app.status === filterStatus;
    const matchesSearch = !searchTerm || 
      app.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.contact_person?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.email?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  if (!isAuthenticated) {
    return (
      <div className="admin-login-page">
        <div className="admin-login-card">
          <div className="admin-login-header">
            <img src="https://customer-assets-cm19k8pv.emergentagent.net/job_tremplin-hub/artifacts/xv6bvpuk_1.png" alt="Trempplin" />
            <h1>Partner Alliance Admin</h1>
            <p>Sign in to manage partner applications</p>
          </div>
          
          {loginError && (
            <div className="admin-login-error">
              <AlertCircle size={18} />
              <span>{loginError}</span>
            </div>
          )}
          
          <form onSubmit={handleLogin} className="admin-login-form">
            <div className="admin-form-group">
              <label>Email</label>
              <input 
                type="email"
                value={loginData.email}
                onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                required
                placeholder="admin@trempplin.com"
              />
            </div>
            <div className="admin-form-group">
              <label>Password</label>
              <input 
                type="password"
                value={loginData.password}
                onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                required
                placeholder="Enter password"
              />
            </div>
            <button type="submit" className="admin-login-btn" disabled={isLoading}>
              {isLoading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <header className="admin-header">
        <div className="admin-header-left">
          <img src="https://customer-assets-cm19k8pv.emergentagent.net/job_tremplin-hub/artifacts/xv6bvpuk_1.png" alt="Trempplin" />
          <h1>Partner Alliance Dashboard</h1>
        </div>
        <div className="admin-header-right">
          <button onClick={() => fetchData(token)} className="admin-btn-icon" title="Refresh">
            <RefreshCw size={18} />
          </button>
          <button onClick={handleLogout} className="admin-btn-logout">
            <LogOut size={18} />
            <span>Logout</span>
          </button>
        </div>
      </header>

      <div className="admin-content">
        {/* Stats Cards */}
        {stats && (
          <div className="admin-stats-grid">
            <div className="admin-stat-card">
              <div className="admin-stat-icon blue">
                <FileText size={24} />
              </div>
              <div className="admin-stat-info">
                <span className="admin-stat-value">{stats.total_applications || 0}</span>
                <span className="admin-stat-label">Total Applications</span>
              </div>
            </div>
            <div className="admin-stat-card">
              <div className="admin-stat-icon yellow">
                <Clock size={24} />
              </div>
              <div className="admin-stat-info">
                <span className="admin-stat-value">{stats.by_status?.new || 0}</span>
                <span className="admin-stat-label">New Applications</span>
              </div>
            </div>
            <div className="admin-stat-card">
              <div className="admin-stat-icon green">
                <CheckCircle2 size={24} />
              </div>
              <div className="admin-stat-info">
                <span className="admin-stat-value">{stats.by_status?.active || 0}</span>
                <span className="admin-stat-label">Active Partners</span>
              </div>
            </div>
            <div className="admin-stat-card">
              <div className="admin-stat-icon teal">
                <DollarSign size={24} />
              </div>
              <div className="admin-stat-info">
                <span className="admin-stat-value">₹{((stats.total_invoiced || 0) / 100000).toFixed(1)}L</span>
                <span className="admin-stat-label">Total Invoiced</span>
              </div>
            </div>
            <div className="admin-stat-card">
              <div className="admin-stat-icon gold">
                <DollarSign size={24} />
              </div>
              <div className="admin-stat-info">
                <span className="admin-stat-value">₹{((stats.total_collected || 0) / 100000).toFixed(1)}L</span>
                <span className="admin-stat-label">Collected</span>
              </div>
            </div>
          </div>
        )}

        {/* Filters and Actions */}
        <div className="admin-toolbar">
          <div className="admin-search">
            <Search size={18} />
            <input 
              type="text"
              placeholder="Search by company, contact, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="admin-filters">
            <select 
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="new">New</option>
              <option value="under_review">Under Review</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="agreement_pending">Agreement Pending</option>
              <option value="payment_pending">Payment Pending</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
            </select>
            <button onClick={handleExportCSV} className="admin-btn-export">
              <Download size={18} />
              <span>Export CSV</span>
            </button>
          </div>
        </div>

        {/* Applications Table */}
        <div className="admin-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Company</th>
                <th>Contact</th>
                <th>Location</th>
                <th>Status</th>
                <th>Applied</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredApplications.length === 0 ? (
                <tr>
                  <td colSpan="6" className="admin-empty">
                    No applications found
                  </td>
                </tr>
              ) : (
                filteredApplications.map((app) => (
                  <tr key={app.id}>
                    <td>
                      <div className="admin-company-cell">
                        <strong>{app.company_name}</strong>
                        <span>{app.company_size} employees</span>
                      </div>
                    </td>
                    <td>
                      <div className="admin-contact-cell">
                        <span>{app.contact_person}</span>
                        <span className="admin-email">{app.email}</span>
                      </div>
                    </td>
                    <td>{app.city}, {app.country}</td>
                    <td>
                      <span 
                        className="admin-status-badge"
                        style={{ backgroundColor: getStatusColor(app.status) + '20', color: getStatusColor(app.status) }}
                      >
                        {getStatusLabel(app.status)}
                      </span>
                    </td>
                    <td>{new Date(app.created_at).toLocaleDateString()}</td>
                    <td>
                      <div className="admin-actions">
                        <button 
                          onClick={() => { setSelectedApp(app); setEditMode(false); }}
                          className="admin-btn-view"
                          title="View Details"
                        >
                          <Eye size={16} />
                        </button>
                        <button 
                          onClick={() => { setSelectedApp(app); setEditMode(true); setUpdateData({ status: app.status, internal_notes: app.internal_notes || '' }); }}
                          className="admin-btn-edit"
                          title="Edit"
                        >
                          <Edit2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail/Edit Modal */}
      {selectedApp && (
        <div className="admin-modal-overlay" onClick={() => { setSelectedApp(null); setEditMode(false); }}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>{editMode ? 'Edit Application' : 'Application Details'}</h2>
              <button onClick={() => { setSelectedApp(null); setEditMode(false); }} className="admin-modal-close">
                <XCircle size={24} />
              </button>
            </div>
            
            <div className="admin-modal-body">
              {editMode ? (
                <div className="admin-edit-form">
                  <div className="admin-form-group">
                    <label>Status</label>
                    <select 
                      value={updateData.status || selectedApp.status}
                      onChange={(e) => setUpdateData({...updateData, status: e.target.value})}
                    >
                      <option value="new">New</option>
                      <option value="under_review">Under Review</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                      <option value="agreement_pending">Agreement Pending</option>
                      <option value="payment_pending">Payment Pending</option>
                      <option value="active">Active</option>
                      <option value="expired">Expired</option>
                    </select>
                  </div>
                  
                  <div className="admin-form-row">
                    <div className="admin-form-group">
                      <label>Membership Start</label>
                      <input 
                        type="date"
                        value={updateData.membership_start || selectedApp.membership_start || ''}
                        onChange={(e) => setUpdateData({...updateData, membership_start: e.target.value})}
                      />
                    </div>
                    <div className="admin-form-group">
                      <label>Membership Expiry</label>
                      <input 
                        type="date"
                        value={updateData.membership_expiry || selectedApp.membership_expiry || ''}
                        onChange={(e) => setUpdateData({...updateData, membership_expiry: e.target.value})}
                      />
                    </div>
                  </div>
                  
                  <div className="admin-form-row">
                    <div className="admin-form-group">
                      <label>Invoice Amount (₹)</label>
                      <input 
                        type="number"
                        value={updateData.invoice_amount || selectedApp.invoice_amount || ''}
                        onChange={(e) => setUpdateData({...updateData, invoice_amount: parseFloat(e.target.value)})}
                        placeholder="500000"
                      />
                    </div>
                    <div className="admin-form-group">
                      <label>Invoice Date</label>
                      <input 
                        type="date"
                        value={updateData.invoice_date || selectedApp.invoice_date || ''}
                        onChange={(e) => setUpdateData({...updateData, invoice_date: e.target.value})}
                      />
                    </div>
                  </div>
                  
                  <div className="admin-form-row">
                    <div className="admin-form-group">
                      <label>Payment Received (₹)</label>
                      <input 
                        type="number"
                        value={updateData.payment_received || selectedApp.payment_received || ''}
                        onChange={(e) => setUpdateData({...updateData, payment_received: parseFloat(e.target.value)})}
                        placeholder="500000"
                      />
                    </div>
                    <div className="admin-form-group">
                      <label>Payment Date</label>
                      <input 
                        type="date"
                        value={updateData.payment_date || selectedApp.payment_date || ''}
                        onChange={(e) => setUpdateData({...updateData, payment_date: e.target.value})}
                      />
                    </div>
                  </div>
                  
                  <div className="admin-form-group">
                    <label>Internal Notes</label>
                    <textarea 
                      value={updateData.internal_notes || selectedApp.internal_notes || ''}
                      onChange={(e) => setUpdateData({...updateData, internal_notes: e.target.value})}
                      rows="4"
                      placeholder="Add internal notes..."
                    ></textarea>
                  </div>
                  
                  <div className="admin-modal-actions">
                    <button onClick={() => { setEditMode(false); setUpdateData({}); }} className="admin-btn-secondary">
                      Cancel
                    </button>
                    <button onClick={handleUpdateApplication} className="admin-btn-primary">
                      Save Changes
                    </button>
                  </div>
                </div>
              ) : (
                <div className="admin-detail-view">
                  <div className="admin-detail-section">
                    <h3>Company Information</h3>
                    <div className="admin-detail-grid">
                      <div className="admin-detail-item">
                        <Building2 size={16} />
                        <div>
                          <label>Company Name</label>
                          <span>{selectedApp.company_name}</span>
                        </div>
                      </div>
                      <div className="admin-detail-item">
                        <Globe size={16} />
                        <div>
                          <label>Website</label>
                          <span>{selectedApp.website || 'Not provided'}</span>
                        </div>
                      </div>
                      <div className="admin-detail-item">
                        <Calendar size={16} />
                        <div>
                          <label>Established</label>
                          <span>{selectedApp.year_established || 'Not provided'}</span>
                        </div>
                      </div>
                      <div className="admin-detail-item">
                        <MapPin size={16} />
                        <div>
                          <label>Location</label>
                          <span>{selectedApp.city}, {selectedApp.country}</span>
                        </div>
                      </div>
                      <div className="admin-detail-item full">
                        <MapPin size={16} />
                        <div>
                          <label>Address</label>
                          <span>{selectedApp.registered_address}</span>
                        </div>
                      </div>
                      <div className="admin-detail-item">
                        <FileText size={16} />
                        <div>
                          <label>GST Number</label>
                          <span>{selectedApp.gst_number || 'Not provided'}</span>
                        </div>
                      </div>
                      <div className="admin-detail-item">
                        <Users size={16} />
                        <div>
                          <label>Company Size</label>
                          <span>{selectedApp.company_size}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="admin-detail-section">
                    <h3>Contact Person</h3>
                    <div className="admin-detail-grid">
                      <div className="admin-detail-item">
                        <Users size={16} />
                        <div>
                          <label>Name & Designation</label>
                          <span>{selectedApp.contact_person} - {selectedApp.designation}</span>
                        </div>
                      </div>
                      <div className="admin-detail-item">
                        <Mail size={16} />
                        <div>
                          <label>Email</label>
                          <span>{selectedApp.email}</span>
                        </div>
                      </div>
                      <div className="admin-detail-item">
                        <Phone size={16} />
                        <div>
                          <label>Mobile</label>
                          <span>{selectedApp.mobile}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="admin-detail-section">
                    <h3>Capabilities</h3>
                    <div className="admin-detail-grid">
                      <div className="admin-detail-item full">
                        <div>
                          <label>Technology Expertise</label>
                          <span>{selectedApp.technology_expertise}</span>
                        </div>
                      </div>
                      <div className="admin-detail-item full">
                        <div>
                          <label>Industry Experience</label>
                          <span>{selectedApp.industry_experience}</span>
                        </div>
                      </div>
                      <div className="admin-detail-item">
                        <div>
                          <label>Partnership Model</label>
                          <span>{selectedApp.partnership_model}</span>
                        </div>
                      </div>
                      <div className="admin-detail-item">
                        <div>
                          <label>Technical Consultants</label>
                          <span>{selectedApp.technical_consultants || 'Not specified'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {selectedApp.additional_comments && (
                    <div className="admin-detail-section">
                      <h3>Additional Comments</h3>
                      <p className="admin-detail-text">{selectedApp.additional_comments}</p>
                    </div>
                  )}
                  
                  {selectedApp.internal_notes && (
                    <div className="admin-detail-section">
                      <h3>Internal Notes</h3>
                      <p className="admin-detail-text internal">{selectedApp.internal_notes}</p>
                    </div>
                  )}
                  
                  <div className="admin-modal-actions">
                    <button onClick={() => setEditMode(true)} className="admin-btn-primary">
                      <Edit2 size={16} />
                      Edit Application
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;
